/**
 * The rules that are true on every route, at phone width.
 *
 * The app carries no stylesheet — each surface styles itself inline — so the
 * handful of rules that can only be written as media queries live in scoped
 * `<style>` blocks, one per surface. This is the shared one: App mounts it
 * once, and every view can reach for the utility classes below rather than
 * inventing its own breakpoint.
 *
 * `!important` on nearly all of it, deliberately: an inline style beats a
 * stylesheet rule unless the rule is important, and inline is where all the
 * layout lives. Nothing here fires above `MOBILE_MAX`.
 */

import { useEffect, useSyncExternalStore } from 'react'

/**
 * The one breakpoint. 720px is under the narrow side of a small tablet and
 * comfortably over the wide side of a large phone, so it separates "this is
 * a column of content" from "this is a page with columns".
 */
export const MOBILE_MAX = 720

/** Height of the phone tab bar, before the home-indicator inset below it. */
export const MOBILE_TABBAR_H = 60

const MOBILE_QUERY = `(max-width: ${MOBILE_MAX}px)`

function subscribe(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {}
  const mq = window.matchMedia(MOBILE_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

function snapshot(): boolean {
  // jsdom has no matchMedia; tests render the desktop layout.
  return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches
}

/**
 * For the places where the phone layout is a different tree, not the same
 * tree restyled: the app shell, bottom sheets, the one-week people grid.
 * Anything a media query can do stays in the stylesheet below.
 */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false)
}

/**
 * Publishes the visible part of the screen as `--keystone-vvh` (height) and
 * `--keystone-vvtop` (how far iOS has panned it down). iOS keeps the layout
 * viewport full height when the keyboard opens, so a layout sized from these
 * is the only way to keep a composer and the content above it in view.
 */
export function useVisualViewportVars(enabled: boolean): void {
  useEffect(() => {
    const vv = window.visualViewport
    if (!enabled || !vv) return
    const root = document.documentElement.style
    const update = (): void => {
      root.setProperty('--keystone-vvh', `${vv.height}px`)
      root.setProperty('--keystone-vvtop', `${vv.offsetTop}px`)
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      root.removeProperty('--keystone-vvh')
      root.removeProperty('--keystone-vvtop')
    }
  }, [enabled])
}

export const responsiveStyles = `
/* Nothing may push the viewport sideways. Canvases (the WebGL scenes) size
   themselves from their container and images arrive from outside the app. */
img, canvas { max-width: 100%; }

@media (max-width: ${MOBILE_MAX}px) {
  /* Page gutters. 28px on each edge is a sixth of a phone's width. */
  .keystone-pad { padding-left: 16px !important; padding-right: 16px !important; }

  /* iOS Safari zooms the page in when a control smaller than 16px takes
     focus, and does not zoom back out afterwards. Every input in the app is
     12–13px, which is right on a desktop and a trap on a phone. */
  input, select, textarea { font-size: 16px !important; }

  /* For anything costing more room than it earns on a phone. */
  .keystone-sm-hide { display: none !important; }

  /* Fixed multi-column rows fall back to "the name, and one thing beside
     it"; the columns that drop out carry .keystone-sm-hide. */
  .keystone-sm-row { grid-template-columns: minmax(0, 1fr) auto !important; gap: 12px !important; }

  /* Space the fixed tab bar takes at the bottom of the screen. Anything
     sticky to the bottom (the chat composer) sits on top of it. */
  :root { --keystone-tabbar-h: calc(${MOBILE_TABBAR_H}px + env(safe-area-inset-bottom)); }
}

/* While the on-screen keyboard is up, the tab bar goes. iOS does not shrink
   the layout viewport for the keyboard, so a fixed bar at its bottom, with the
   composer stuck on top of it, leaves the page panned to a strip of blank
   space until the keyboard is dismissed. Zeroing the variable also drops the
   main padding and seats the composer at the bottom. */
@media (max-width: ${MOBILE_MAX}px) and (hover: none) and (pointer: coarse) {
  :root:has(textarea:focus, input:focus:not([type=checkbox], [type=radio], [type=button], [type=submit])) {
    --keystone-tabbar-h: 0px;
  }
  :root:has(textarea:focus, input:focus:not([type=checkbox], [type=radio], [type=button], [type=submit])) .keystone-tabbar {
    display: none !important;
  }
}
`
